// Exportación a Excel con SheetJS (xlsx).
//
// Limitaciones conocidas:
//   - El paquete público de SheetJS (`xlsx`) no soporta estilos de
//     celda (negritas, colores, bordes) sin la versión Pro.
//     Aplicamos sólo lo que SÍ es portable: anchos de columna
//     automáticos y orden de columnas significativo. El "header en
//     negrita / gris medio" mencionado en el plan no es alcanzable
//     con la versión libre — anotado en BITACORA para revisar en v1.1
//     con `exceljs` si se prioriza estilo visual.
//
// API pública:
//   - exportInventoryToXlsx(documents, validationCache, filename?)
//   - exportClientReportToXlsx(clienteId, clientData, filename?)
//
// Donde `documents` es la lista enriquecida del inventario (con
// `metadata`), `validationCache` es el objeto completo de
// validacion.json (puede ser null) y `clientData` es:
//   { clientFiles, secciones, porFase, hitos }

import * as XLSX from 'xlsx'

const MAX_COL_WIDTH = 80
const MIN_COL_WIDTH = 12

function fmtDateTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('es-MX')
}

function fmtDate(iso) {
  if (!iso) return ''
  const d = iso.length === 10 ? new Date(`${iso}T00:00:00`) : new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('es-MX')
}

function todayStamp() {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Aplica anchos de columna a una hoja a partir del max length del
 * valor por columna. Los keys del primer objeto definen el orden.
 */
function applyColumnWidths(ws, rows) {
  if (!rows || rows.length === 0) return
  const cols = Object.keys(rows[0])
  ws['!cols'] = cols.map((col) => {
    let maxLen = String(col).length
    for (const row of rows) {
      const len = String(row[col] ?? '').length
      if (len > maxLen) maxLen = len
    }
    const wch = Math.min(Math.max(maxLen + 2, MIN_COL_WIDTH), MAX_COL_WIDTH)
    return { wch }
  })
}

function addSheet(wb, rows, name) {
  if (!rows || rows.length === 0) {
    const empty = XLSX.utils.aoa_to_sheet([['Sin datos para esta vista.']])
    empty['!cols'] = [{ wch: 40 }]
    XLSX.utils.book_append_sheet(wb, empty, name)
    return
  }
  const ws = XLSX.utils.json_to_sheet(rows)
  applyColumnWidths(ws, rows)
  XLSX.utils.book_append_sheet(wb, ws, name)
}

/**
 * Excel restringe los nombres de hoja a 31 chars máx y prohíbe ciertos
 * caracteres. NO usar esto sobre filenames — truncaría la extensión.
 */
function safeSheetName(input) {
  return String(input || 'Hoja').replace(/[\\/?*\[\]]/g, ' ').slice(0, 31)
}

/**
 * Asegura que el filename tenga extensión .xlsx (insensible a mayúsculas).
 * Útil para evitar que el navegador descargue sin extensión por colisión
 * con nombres previos.
 */
function ensureXlsxExtension(filename) {
  if (!filename) return 'archivo.xlsx'
  return /\.xlsx$/i.test(filename) ? filename : `${filename}.xlsx`
}

// ---------------------------------------------------------------------------
// Inventario completo
// ---------------------------------------------------------------------------

function buildInventoryRows(documents, validationCache) {
  const docs = validationCache?.documentos || {}
  return documents.map((doc) => {
    const entry = doc?.id ? docs[doc.id] : null
    const meta = doc.metadata || {}
    return {
      'Drive Item ID': doc.id || '',
      Nombre: doc.name || '',
      Ruta: doc.relativePath || '',
      Fase: meta.fase || '',
      Audiencia: meta.audiencia || '',
      Cliente: meta.cliente || '',
      Versión: meta.version || '',
      'Código E-XX': meta.codigoEntregable || '',
      'Sección AFAC': meta.seccionAFAC
        ? `Sección ${String(meta.seccionAFAC).padStart(2, '0')}`
        : '',
      'Es plantilla': meta.esPlantilla ? 'Sí' : 'No',
      Estado: entry?.estado || 'Sin estado',
      Notas: entry?.notas || '',
      'Modificado por': entry?.modificadoPor || '',
      'Email modificador': entry?.modificadoPorEmail || '',
      'Última modificación (validación)': fmtDateTime(entry?.ultimaModificacion),
      'Última modificación (SharePoint)': fmtDateTime(doc.lastModifiedDateTime),
      'Tamaño (bytes)': typeof doc.size === 'number' ? doc.size : ''
    }
  })
}

function buildMetadataRows(documents, validationCache, opts = {}) {
  const totalDocs = documents.length
  const docs = validationCache?.documentos || {}
  let conEstado = 0
  let aprobados = 0
  for (const d of documents) {
    const entry = d?.id ? docs[d.id] : null
    if (entry?.estado) {
      conEstado += 1
      if (entry.estado === 'Aprobado') aprobados += 1
    }
  }
  return [
    { Campo: 'Generado', Valor: fmtDateTime(new Date().toISOString()) },
    { Campo: 'Total documentos en el export', Valor: totalDocs },
    { Campo: 'Con estado registrado', Valor: conEstado },
    { Campo: 'Aprobados', Valor: aprobados },
    {
      Campo: 'Filtros aplicados',
      Valor: opts.filtersDescription || '(ninguno — inventario completo)'
    },
    {
      Campo: 'Fuente',
      Valor: 'Dashboard JMA AFAC (https://afac.jmacybersec.com)'
    },
    {
      Campo: 'Schema validacion.json',
      Valor: validationCache?.version || 'no cargado'
    }
  ]
}

/**
 * Genera y descarga un .xlsx con el inventario actual (o filtrado).
 *
 * @param {Array<object>} documents Lista enriquecida (con `metadata`).
 * @param {object|null} validationCache Cache de validacion.json.
 * @param {object} [opts]
 * @param {string} [opts.filename]
 * @param {string} [opts.filtersDescription]
 */
export function exportInventoryToXlsx(documents, validationCache, opts = {}) {
  const filename = ensureXlsxExtension(
    opts.filename || `JMA_AFAC_Inventario_${todayStamp()}.xlsx`
  )
  const rows = buildInventoryRows(documents, validationCache)
  const wb = XLSX.utils.book_new()
  addSheet(wb, rows, 'Inventario')
  addSheet(
    wb,
    buildMetadataRows(documents, validationCache, opts),
    'Metadatos'
  )
  XLSX.writeFile(wb, filename)
  return filename
}

// ---------------------------------------------------------------------------
// Reporte por cliente
// ---------------------------------------------------------------------------

function buildClientResumenRows(clienteId, clientData) {
  const { clientFiles = [], secciones = [], hitos = [] } = clientData
  const docs = clientFiles
  const sec = secciones
  const aprobados = docs.filter(
    (d) => d?.entry?.estado === 'Aprobado'
  ).length
  return [
    { Campo: 'Cliente', Valor: clienteId },
    { Campo: 'Fecha de reporte', Valor: fmtDateTime(new Date().toISOString()) },
    { Campo: 'Total documentos del cliente', Valor: docs.length },
    { Campo: 'Con estado registrado', Valor: docs.filter((d) => d?.entry?.estado).length },
    { Campo: 'Aprobados', Valor: aprobados },
    { Campo: 'Secciones verdes (completas)', Valor: sec.filter((s) => s.estado === 'verde').length },
    { Campo: 'Secciones ámbar (parciales)', Valor: sec.filter((s) => s.estado === 'ambar').length },
    { Campo: 'Secciones rojas (sin docs o solo placeholders)', Valor: sec.filter((s) => s.estado === 'rojo').length },
    { Campo: 'Secciones externas (pendiente entrega UVA / autoridad)', Valor: sec.filter((s) => s.estado === 'externa').length },
    { Campo: 'Hitos totales', Valor: hitos.length },
    { Campo: 'Hitos pendientes', Valor: hitos.filter((h) => !h.completado).length },
    { Campo: 'Hitos completados', Valor: hitos.filter((h) => !!h.completado).length },
    { Campo: 'Fuente', Valor: 'Dashboard JMA AFAC' }
  ]
}

function buildSeccionesRows(secciones) {
  return secciones.map((s) => ({
    Sección: String(s.numero).padStart(2, '0'),
    Nombre: s.nombre,
    'Responsabilidad externa': s.noJMA ? 'Sí' : 'No',
    Estado: s.estado,
    Mensaje: s.mensaje,
    'Aprobados / Total': `${s.aprobados}/${s.total}`,
    'Documentos en sección': s.docs.length
  }))
}

/**
 * Construye la hoja con TODOS los documentos del cliente, ordenados por
 * fase (los sin fase identificada quedan al final). Antes filtraba sólo
 * por `porFase`, lo que dejaba fuera los documentos cuya ruta no incluye
 * `Fase_X_*` (típico de los entregables de la Carpeta AFAC del cliente).
 */
function buildAllClientDocsRows(clientFiles) {
  const SIN_FASE = 'Sin fase'
  const sorted = [...(clientFiles || [])].sort((a, b) => {
    const fa = a.metadata?.fase || `zzz-${SIN_FASE}`
    const fb = b.metadata?.fase || `zzz-${SIN_FASE}`
    if (fa !== fb) {
      return fa.localeCompare(fb, 'es', { numeric: true, sensitivity: 'base' })
    }
    return (a.name || '').localeCompare(b.name || '', 'es')
  })
  return sorted.map((doc) => {
    const entry = doc?.entry
    return {
      Fase: doc.metadata?.fase || SIN_FASE,
      Nombre: doc.name,
      'Código E-XX': doc.metadata?.codigoEntregable || '',
      'Sección AFAC': doc.metadata?.seccionAFAC
        ? `Sección ${String(doc.metadata.seccionAFAC).padStart(2, '0')}`
        : '',
      Versión: doc.metadata?.version || '',
      Audiencia: doc.metadata?.audiencia || '',
      'Es plantilla': doc.metadata?.esPlantilla ? 'Sí' : 'No',
      Estado: entry?.estado || 'Sin estado',
      Notas: entry?.notas || '',
      'Modificado por': entry?.modificadoPor || '',
      'Última modificación (validación)': fmtDateTime(entry?.ultimaModificacion),
      Ruta: doc.relativePath || ''
    }
  })
}

function buildHitosRows(hitos) {
  return (hitos || []).map((h) => ({
    Estado: h.completado ? 'Completado' : 'Pendiente',
    Descripción: h.descripcion || '',
    'Fecha objetivo': fmtDate(h.fechaObjetivo),
    Responsable: h.responsable || '',
    Creado: fmtDateTime(h.creado),
    'Creado por': h.creadoPor || '',
    'Completado en': fmtDateTime(h.completadoEn)
  }))
}

/**
 * Genera y descarga un .xlsx con el reporte ejecutivo de un cliente.
 *
 * @param {string} clienteId
 * @param {object} clientData { clientFiles, secciones, porFase, hitos }
 *   Donde `clientFiles` y `porFase` traen cada doc enriquecido con
 *   `entry` (entrada del validation-store) además de su `metadata`.
 * @param {object} [opts]
 * @param {string} [opts.filename]
 */
export function exportClientReportToXlsx(clienteId, clientData, opts = {}) {
  const filename = ensureXlsxExtension(
    opts.filename ||
      `JMA_AFAC_Reporte_${clienteId}_${todayStamp()}.xlsx`
  )
  const wb = XLSX.utils.book_new()
  addSheet(wb, buildClientResumenRows(clienteId, clientData), 'Resumen')
  addSheet(wb, buildSeccionesRows(clientData.secciones || []), 'Carpeta AFAC')
  addSheet(
    wb,
    buildAllClientDocsRows(clientData.clientFiles || []),
    'Documentos por fase'
  )
  addSheet(wb, buildHitosRows(clientData.hitos || []), 'Hitos')
  // OJO: writeFile NO debe recibir safeSheetName(filename) — esa función
  // está pensada para nombres de hoja (slice 31 chars) y trunca la
  // extensión .xlsx. Aquí va el filename completo tal cual.
  XLSX.writeFile(wb, filename)
  return filename
}
